<template>
  <el-dialog title="Create a wheel" :visible.sync="open" :before-close="close" width="390px">
    <div class="flex flex-wrap mb-6">
      <div class="w-full mb-6">
        <label for="name" class="mb-2 font-bold block">{{ $t('wheel.create.fields.name') }}</label>
        <input
          class="py-2 px-4 block w-full bg-gray-100 border border-gray-100 rounded focus:outline-none focus:bg-white focus:border-gray-500"
          id="name"
          v-model="fields.name"
          type="text"
          @input="checkInputs()"
          placeholder="Welcome tour"
          required
        />
      </div>

      <div class="w-full mb-6">
        <label for="date" class="mb-2 font-bold block">{{ $t('wheel.create.fields.date.name') }}</label>
        <div>
          <el-date-picker
            v-model="period"
            type="daterange"
            id="date"
            :start-placeholder="$t('wheel.create.fields.date.start')"
            :end-placeholder="$t('wheel.create.fields.date.end')"
            @change="onPeriodSelected"
            required
          />
        </div>
      </div>

      <div class="w-full mb-6">
        <label for="url_start" class="mb-2 font-bold block">{{ $t('wheel.create.fields.where') }}</label>
        <input
          class="py-2 px-4 block w-full bg-gray-100 border border-gray-100 rounded focus:outline-none focus:bg-white focus:border-gray-500"
          id="url_start"
          @input="checkInputs()"
          v-on:blur="checkGoodDomain()"
          v-model="fields.url"
          type="text"
          :placeholder="_extractHostname(site.url)"
          required
        />
      </div>
    </div>
    <div class="flex flex-row-reverse">
      <button
        @click="save()"
        :disabled="!canSave"
        class="loader-button px-4 py-2 gap-2 rounded text-blue-50 max-w-max shadow-sm hover:shadow-md"
        :class="{ 'bg-green-300': !canSave, 'bg-green-500': canSave }"
      >
        {{ $t('wheel.actions.next') }}
        <font-awesome-icon icon="arrow-right" />
      </button>
      <button
        @click="close"
        class="px-4 py-2 rounded border border-gray-200 text-gray-500 max-w-max shadow-sm hover:shadow-md mr-3"
      >
        {{ $t('wheel.actions.cancel') }}
      </button>
    </div>
  </el-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'FormWheelCreate',
  props: {
    open: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      fields: {
        name: null,
        url: null,
        activationdate: null,
        deactivationdate: null
      },
      loading: false,
      idCreated: null,
      canSave: false,
      period: []
    }
  },
  computed: {
    ...mapGetters('account', {
      site: 'currentSite'
    })
  },
  methods: {
    ...mapActions('wheel', ['addWheel']),
    onPeriodSelected() {
      if (this.period[0]) {
        this.fields.activationdate = this.period[0]
        this.fields.deactivationdate = this.period[1]
      } else {
        this.fields.activationdate = null
        this.fields.deactivationdate = null
      }
      this.checkInputs()
    },
    save() {
      if (!this.checkInputs()) {
        return this.$notify.info({
          message: this.$t('wheel.notification.error'),
          title: 'Warning',
          type: 'warning'
        })
      }
      this.loading = true
      this.addWheel({ ...this.fields })
        .then(async wheel => {
          this.$notify({
            title: 'Success',
            message: this.$root.$t('notifications.wheel.added'),
            type: 'success'
          })
          this.close()
          this.idCreated = wheel.id
          this.$router.push({
            name: 'Wheel',
            params: { site: this.$route.params.site, id: wheel.id },
            query: { add_step: 1 }
          })
          this.loading = false
        })
        .catch(response => {
          console.log('error', response)
          this.$notify.error({
            title: this.$root.$t('api.' + response.data.code + '.title'),
            message: this.$root.$t('api.' + response.data.code + '.message')
          })
          this.loading = false
        })
    },

    checkGoodDate() {
      try {
        this.fields.activationdate = this._setDate(this.fields.activationdate, this.site.timezone, true)
        this.fields.deactivationdate = this._setDate(this.fields.deactivationdate, this.site.timezone, false)
      } catch (e) {
        console.error(e)
      }
    },

    checkGoodDomain() {
      if (this.fields.url && this.fields.url.startsWith('https://')) {
        this.fields.url = this.fields.url.slice(8)
      }
      if (this.fields.url && this.fields.url.startsWith('http://')) {
        this.fields.url = this.fields.url.slice(7)
      }
      if (this.fields.url && this.fields.url.endsWith('/')) {
        this.fields.url = this.fields.url.slice(0, -1)
      }
      let i = 0
      while (this.fields.url && this.fields.url[i] !== '/' && i <= this.fields.url.length) {
        ++i
      }
      if (this.fields.url) {
        this.fields.url = this.fields.url.slice(0, i)
      }
    },
    checkInputs() {
      this.checkGoodDomain()
      this.checkGoodDate()
      const ValidHostnameRegex = /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9\-]*[A-Za-z0-9])$/
      this.canSave = true

      if (
        !this.fields.deactivationdate ||
        !this.fields.activationdate ||
        this.fields.deactivationdate.includes('Invalid date') ||
        this.fields.activationdate.includes('Invalid date')
      ) {
        this.canSave = false
      }
      if (!this.fields.name) {
        this.canSave = false
      }
      if (
        !this.fields.url ||
        !Array.isArray(this.fields.url.match(ValidHostnameRegex)) ||
        (Array.isArray(this.fields.url.match(ValidHostnameRegex)) && !this.fields.url.match(ValidHostnameRegex).length)
      ) {
        this.canSave = false
      }
      return this.canSave
    },
    close() {
      if (this.loading === false) {
        return this.$emit('close', true)
      }
    }
  }
}
</script>

<style>
.loader-button {
  display: flex;
  align-items: center;
}

.loader-button img {
  margin-right: 8px;
}
</style>



// WEBPACK FOOTER //
// src/components/wheels/ui/modal/create.vue